import { attachmentSchema } from "@/api/attachment";
import { uploadImage } from "@/api/image.ts";
import { Button } from "@/components/ui/button.tsx";
import { FormControl, FormField, FormItem } from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Slider } from "@/components/ui/slider.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { getCroppedImg, getRotatedImage } from "@/lib/canvas.ts";
import { Attachment } from "@/lib/types";
import { readFile, urlToFile } from "@/lib/utils.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { getOrientation } from "get-orientation/browser";
import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { MultilingualField } from "../form-items/MultilingualField";
import { Col } from "../form-items/AbstractForm";

const ORIENTATION_TO_ANGLE = {
  "3": 180,
  "6": 90,
  "8": -90,
};

const imageSchema = attachmentSchema
  .pick({
    textAlt: true,
    caption: true,
    description: true,
  })
  .extend({
    image: z.instanceof(FileList).optional(),
  });

export type ImageFormData = z.infer<typeof imageSchema>;

export type BuildImageNameSignature = (
  instanceName: string,
  docId: string,
  position: string,
  data: ImageFormData,
) => string;

export interface AddCroppedImageFormProps {
  attachment?: Attachment;
  refetch: () => void;
  imagePosition: string;
  aspect?: number;
  onUpdate?: (imageValueElement: any, data: ImageFormData) => void;
  onSuccess?: (attachment: Attachment) => void;
  docId?: string;
  buildImageName: BuildImageNameSignature;
  forceCreateNewAttachment?: boolean;
}

const AddCroppedImageForm = ({
  attachment,
  refetch,
  imagePosition,
  aspect = 9 / 16,
  onUpdate,
  onSuccess,
  docId,
  buildImageName,
  forceCreateNewAttachment = false,
}: AddCroppedImageFormProps) => {
  const { instanceData } = useAuth();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [currentImage, setCurrentImage] = useState<File | null>(null);

  const formImage = useForm<ImageFormData>({
    resolver: zodResolver(imageSchema),
  });

  const { t } = useTranslation();

  const { handleSubmit, control } = formImage;

  const fileRef = formImage.register("image");

  const onCropComplete = (_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const showCroppedImage = async () => {
    try {
      if (imgSrc && croppedAreaPixels) {
        const croppedImage = await getCroppedImg(
          imgSrc,
          croppedAreaPixels,
          rotation,
        );
        if (croppedImage) {
          const file = await urlToFile(
            croppedImage,
            "croppedImage.jpg",
            "image/jpeg",
          );
          setCurrentImage(file);
          setImgSrc(croppedImage);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const UploadImage = async (data: ImageFormData) => {
    if (onUpdate) {
      onUpdate(currentImage, data);
    } else {
      if (currentImage) {
        if (instanceData == null) {
          console.error("Tried to upload an image, but instanceData is null");
          return;
        }

        const attachment = await uploadImage(
          currentImage,
          {
            ...data,
            details: "",
            name:
              buildImageName?.(
                instanceData.name,
                docId ?? instanceData.id,
                imagePosition,
                data,
              ) ?? "",
            docId: docId ?? instanceData.id,
            position: imagePosition,
            published: false,
            instanceId: instanceData.id,
          },
          instanceData.s3Bucket,
          instanceData.id,
          forceCreateNewAttachment ? "back/api/attachment/create" : undefined,
        );
        toast({
          title: "Success",
          description: "imageSuccessMessage",
          duration: 1000,
          variant: "success",
        });
        refetch();
        onSuccess?.(attachment);
      } else {
        toast({
          title: "Error",
          description: "imageErrorMessage",
          duration: 1000,
          variant: "error",
        });
      }
    }
  };

  const loadImage = async () => {
    const image: Attachment | undefined = attachment;
    if (image) {
      formImage.setValue("caption", image.caption);
      formImage.setValue("textAlt", image.textAlt);
      formImage.setValue("description", image.description);
      setImgSrc(image?.mediumPublicUrl || null);
      if (image?.mediumPublicUrl) {
        const file = await urlToFile(
          image.mediumPublicUrl,
          "existingImage.jpg",
          "image/jpeg",
        );
        setCurrentImage(file);
      }
    }
  };

  useEffect(() => {
    loadImage();
  }, [attachment]);

  return (
    <div className="flex gap-8">
      <div className="w-full">
        <div className="relative w-full h-[500px] bg-gray-100 flex items-center justify-center">
          {imgSrc && (
            <Cropper
              image={imgSrc}
              crop={crop}
              rotation={rotation}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          )}
        </div>
        <div className={"w-full mt-2 flex gap-2"}>
          <p className="text-sm font-bold">{t("ajoutImageZoom")}</p>
          <Slider
            defaultValue={[zoom]}
            min={1}
            max={3}
            step={0.1}
            onValueChange={(e) => setZoom(e[0])}
          />
        </div>
        <div className={"w-full mt-2 flex gap-2"}>
          <p className="text-sm font-bold">{t("ajoutImageRotation")}</p>
          <Slider
            defaultValue={[rotation]}
            min={0}
            max={360}
            step={1}
            onValueChange={(e) => setRotation(e[0])}
          />
        </div>
        <Button className="mt-2" onClick={showCroppedImage}>
          {t("ajoutImageBoutonEnregistrer")}
        </Button>
      </div>

      <FormProvider {...formImage}>
        <form
          onSubmit={handleSubmit(UploadImage, () => {
            toast({
              title: "Error",
              description: "imageErrorMessage",
              duration: 1000,
              variant: "error",
            });
          })}
          className="w-[50%] flex flex-col gap-2"
        >
          {currentImage && (
            <img
              src={URL.createObjectURL(currentImage)}
              alt="preview"
              className="w-full h-[200px] object-contain"
            />
          )}
          <FormField
            control={control}
            name={"image"}
            render={() => (
              <FormItem>
                <FormControl>
                  <Input
                    {...fileRef}
                    type="file"
                    id={"file-input"}
                    onChange={async (e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const file = e.target.files[0];
                        let imageDataUrl = await readFile(file);

                        try {
                          // apply rotation if needed
                          const orientation = await getOrientation(file);
                          const rotation =
                            ORIENTATION_TO_ANGLE[
                              orientation as unknown as keyof typeof ORIENTATION_TO_ANGLE
                            ];
                          if (rotation) {
                            imageDataUrl = await getRotatedImage(
                              imageDataUrl as string,
                              rotation,
                            );
                          }
                        } catch (e: any) {
                          console.warn(
                            "failed to detect the orientation : ",
                            e,
                          );
                        }
                        setImgSrc(imageDataUrl as string);
                        setCurrentImage(file);
                        await fileRef.onChange(e);
                      }
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <MultilingualField
            render={(lang) => (
              <Col>
                <FormField
                  control={control}
                  name={`textAlt.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          label={t("addImageTextAltFR")}
                          id={field.name}
                          type="text"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`caption.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          label={t("addImageLegendFR")}
                          id={field.name}
                          type="text"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`description.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          label={t("addImageDescriptionFR")}
                          id={field.name}
                          type="text"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </Col>
            )}
          />
          <Button type="button" onClick={formImage.handleSubmit(UploadImage)}>
            {t("addImageSubmit")}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddCroppedImageForm;
